import MainLayout from "../../../layouts/MainLayout";
import { usePermissions } from "../../../hooks/usePermissions";
import { useNavigate, useParams } from "react-router-dom";
import {
  faEdit,
  faEye,
  faTrash,
  faUndo,
} from "@fortawesome/free-solid-svg-icons";
import ConfirmationModal from "../../../components/modals/ConfirmationModal";
import useBaseService from "../../../hooks/services/useBaseService";
import useLoader from "../../../hooks/useLoader";
import { entities } from "../../../constants/entities";
import PageHeader from "../../../components/PageHeader";
import ListHeader from "../../../components/ListHeader";
import useAction from "../../../hooks/useAction";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Box } from "@mui/material";
import TableWithInactiveFilter from "../../../components/table/TableWithInactiveFilter";
import useUrlFilters from "../../../hooks/useUrlFilters";
import useGenericFilter from "../../../hooks/filters/useGenericFilter";
import useFilterHandlers from "../../../hooks/useFilterHandlers";
import useQueryWithLoader from "../../../hooks/useQueryWithLoader";

const LocationOrganizationPage = () => {
  const { canAccess } = usePermissions();
  const navigate = useNavigate();
  const { organizationId, addressId } = useParams();

  const queryClient = useQueryClient();
  const { get: baseServiceGet, del: baseServiceDel } = useBaseService(navigate);

  const {
    setFilters: applyUrlFilters,
    pagination,
    changePage,
    setPagination,
  } = useUrlFilters({
    area: [],
    section: [],
    spot: [],
    active: null,
  });

  const { handleFilterApply, handleFilterClear } =
    useFilterHandlers(applyUrlFilters);

  const { localFilterValues } = useGenericFilter(
    {
      area: [],
      section: [],
      spot: [],
      active: null,
    },
    handleFilterApply,
    handleFilterClear,
  );

  const { showLoader, hideLoader } = useLoader();

  const {
    openModalConfirmation,
    action,
    showConfirmation,
    handleConfirmAction,
    handleCancelConfirmation,
    selectedItem,
  } = useAction(navigate);

  const {
    data: locationsResponseData,
    isLoading,
    isFetching,
  } = useQueryWithLoader({
    queryKey: ["locationsOrganization", organizationId, addressId],
    queryFn: async () => {
      const response = await baseServiceGet(
        entities.organizations.organizationAddressLocations(
          organizationId,
          addressId,
        ),
      );
      return response.result;
    },
    enabled: !!organizationId && !!addressId,
    select: (data) => {
      return {
        locations: data.data.map((location) => ({
          id: location.id,
          area: location.area,
          section: location.section,
          spot: location.spot,
          isDeleted: !!location.deleted_at,
        })),
        totalPages: data.last_page,
      };
    },
  });

  const { locations = [], totalPages = 1 } = locationsResponseData || {};

  const columns = [
    { key: "area", label: "Área" },
    { key: "section", label: "Seção" },
    { key: "spot", label: "Ponto" },
  ];

  const inactiveMutation = useMutation({
    mutationFn: async (locationId) => {
      showLoader();
      const response = await baseServiceDel(
        entities.organizations.organizationAddressLocationDelete(
          organizationId,
          addressId,
          locationId,
        ),
      );
      return response;
    },
    onSuccess: (response) => {
      if (response?.status === 200) {
        hideLoader();
        queryClient.invalidateQueries({
          queryKey: ["locationsOrganization", organizationId, addressId],
        });
      }
      hideLoader();
    },
    onError: (error) => {
      console.error("onError ", error);
      hideLoader();
    },
  });

  const activateMutation = inactiveMutation;

  const actions = [
    {
      icon: faEye,
      title: "Ver Detalhes",
      buttonClass: "btn-info",
      permission: "Ver endereços de organizações",
      onClick: (location) =>
        navigate(
          `/aluguel/organizacoes/detalhes/${organizationId}/enderecos/${addressId}/localizacoes/detalhes/${location.id}`,
        ),
      show: (location) =>
        !location.isDeleted && canAccess("Ver endereços de organizações"),
    },
    {
      icon: faEdit,
      title: "Editar",
      buttonClass: "btn-primary",
      permission: "Atualizar endereços de organizações",
      onClick: (location) =>
        navigate(
          `/aluguel/organizacoes/detalhes/${organizationId}/enderecos/${addressId}/localizacoes/editar/${location.id}`,
        ),
      show: (location) =>
        !location.isDeleted && canAccess("Atualizar endereços de organizações"),
    },
    {
      icon: faTrash,
      title: "Excluir",
      buttonClass: "btn-danger",
      permission: "Excluir endereços de organizações",
      onClick: (location) =>
        showConfirmation(
          location,
          `Você tem certeza que deseja excluir?`,
          async () => {
            try {
              await inactiveMutation.mutateAsync(location.id);
            } catch (error) {
              console.log("Erro ao excluir localização:", {
                status: error.status,
                message: error.message,
              });
            }
          },
        ),
      show: (location) =>
        !location.isDeleted && canAccess("Excluir endereços de organizações"),
    },
    {
      icon: faUndo,
      title: "Restaurar",
      buttonClass: "btn-info",
      permission: "Excluir endereços de organizações",
      onClick: (location) =>
        showConfirmation(
          location,
          `Você tem certeza que deseja restaurar?`,
          async () => {
            try {
              await activateMutation.mutateAsync(location.id);
            } catch (error) {
              console.log("Erro ao restaurar localização:", {
                status: error.status,
                message: error.message,
              });
            }
          },
        ),
      show: (location) =>
        location.isDeleted && canAccess("Excluir endereços de organizações"),
    },
  ];

  return (
    <MainLayout selectedCompany="ALUCOM">
      <div className="container-fluid p-1">
        <PageHeader
          title="Localizações da Organização"
          showBackButton={true}
          backUrl={`/aluguel/organizacoes/detalhes/${organizationId}/`}
        />

        <ListHeader
          title="Lista de Localizações"
          buttonText="Nova Localização"
          buttonLink={`/aluguel/organizacoes/detalhes/${organizationId}/enderecos/${addressId}/localizacoes/adicionar`}
          canAccess={canAccess}
          permission="Criar endereços de organizações"
        />

        <Box sx={{ mt: 2 }}>
          <TableWithInactiveFilter
            data={locations}
            columns={columns}
            actions={actions}
            totalPages={totalPages}
            currentPage={pagination.page - 1}
            onPageChange={changePage}
            rowsPerPage={pagination.perPage}
            onRowsPerPageChange={(event) => {
              const newPerPage = +event.target.value;
              setPagination({ page: 1, perPage: newPerPage });
            }}
            filterValues={localFilterValues}
            localFilterValues={localFilterValues}
            applyUrlFilters={applyUrlFilters}
            showFilterInactive={false}
            type="default"
            isLoading={isLoading}
            isFetching={isFetching}
          />
        </Box>
      </div>

      <ConfirmationModal
        open={openModalConfirmation}
        onClose={handleCancelConfirmation}
        onConfirm={handleConfirmAction}
        itemName={selectedItem?.area || ""}
        text={action?.text}
      />
    </MainLayout>
  );
};

export default LocationOrganizationPage;
